import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { findIndex, pathOr } from 'ramda'
import { Helmet } from 'react-helmet'
import { subscribe } from 'react-contextual'
import PortfoliosProvider from '../../PortfoliosProvider'
import styles from './PortfolioView.module.scss'
import PropertyInfo from './PropertyInfo/PropertyInfo'
import PortfolioInfo from './PortfolioInfo/PortfolioInfo'
import ContextMenu from '../../../../views/components/molecules/ContextMenu/ContextMenu'
import { portfolioApiEvents, portfolioApiSelectors } from '../../../../modules/api/portfolio'
import Edit from '../../../../views/components/atoms/Icons/Controls/Edit'
import Refresh from '../../../../views/components/atoms/Icons/Controls/Refresh'
import Delete from '../../../../views/components/atoms/Icons/Controls/Delete'
import Statement from '../../../../views/components/atoms/Icons/Doc/Statement'
import RefundProvider from '../../../depositManagement/RefundProvider'
import { DimmerLoader } from '../../../../views/components'
import { propertyApiEvents } from '../../../api/property'
import Close from 'views/components/atoms/Svgs/Close'

const PortfolioView = ({
  portfolioViewId,
  redirect,
  closeProperty,
  viewData,
  selectedPortfolio,
  portfolioStatus,
  openEditPortfolio,
  openRenewLease,
  openEditProperty,
  openPortfolioStatement,
  deleteDraftLease,
  openPortfolio,
  openLeaseTermination,
  isPortfolioViewLoading,
  resetCurrentPortfolio,
}: any) => {
  const dispatch = useDispatch()
  const portfolio = viewData.portfolios[selectedPortfolio]

  const getSummaryFieldById = useSelector(portfolioApiSelectors.getSummaryFieldById)

  useEffect(() => {
    const propertyId = getSummaryFieldById(portfolioViewId, ['propertyId'])
    resetCurrentPortfolio()
    dispatch(portfolioApiEvents.portfolio_request(portfolioViewId))
    if (propertyId) {
      dispatch(propertyApiEvents.property_request({ id: propertyId }))
      dispatch(propertyApiEvents.propertyPortfolios_request({ id: propertyId }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioViewId, dispatch])

  const tenantId = useSelector(state => portfolioApiSelectors.getPrimaryTenantByPortfolioId(state)(portfolioViewId))

  const getSelectedPortfolioIndex = (portfolioId: any) =>
    findIndex((portfolio: any) => portfolio.id === portfolioId, viewData.leaseSelectorOptions)

  let selectedPortfolioIndex = getSelectedPortfolioIndex(portfolioViewId)

  let leaseOptions: any = []

  switch (portfolioStatus) {
    case 'active':
    case 'renewal':
      leaseOptions = [
        {
          label: 'Lease statement',
          element: <Statement />,
          onSelect: () => openPortfolioStatement(portfolioViewId),
        },
        {
          label: portfolioStatus === 'renewal' ? 'Edit renewal' : 'Renew lease',
          element: <Refresh />,
          onSelect: () => openRenewLease(portfolioViewId),
        },
        {
          label: 'Edit lease',
          element: <Edit />,
          onSelect: () => openEditPortfolio(portfolioViewId),
        },

        {
          label: 'Terminate lease',
          element: <Delete />,
          onSelect: () => openLeaseTermination(portfolioViewId),
        },
      ]
      break
    case 'expiring':
      leaseOptions = [
        {
          label: 'Lease statement',
          element: <Statement />,
          onSelect: () => openPortfolioStatement(portfolioViewId),
        },
        {
          label: 'Renew lease',
          element: <Refresh />,
          onSelect: () => openRenewLease(portfolioViewId),
        },
        {
          label: 'Edit lease',
          element: <Edit />,
          onSelect: () => openEditPortfolio(portfolioViewId),
        },
        {
          label: 'Terminate lease',
          element: <Delete />,
          onSelect: () => openLeaseTermination(portfolioViewId),
        },
      ]
      break
    case 'expired':
      leaseOptions = [
        {
          label: 'Lease statement',
          element: <Statement />,
          onSelect: () => openPortfolioStatement(portfolioViewId),
        },
        {
          label: 'Renew lease',
          element: <Refresh />,
          onSelect: () => openRenewLease(portfolioViewId),
        },
      ]
      break
    case 'terminated':
      leaseOptions = [
        {
          label: 'Lease statement',
          element: <Statement />,
          onSelect: () => openPortfolioStatement(portfolioViewId),
        },
      ]
      break
    case 'draft':
      leaseOptions = [
        {
          label: 'Edit lease',
          element: <Edit />,
          onSelect: () => openEditPortfolio(portfolioViewId),
        },
        {
          label: 'Edit property',
          element: <Edit />,
          onSelect: () => openEditProperty(portfolioViewId),
        },
        {
          label: 'Delete draft',
          element: <Delete />,
          onSelect: () => {
            deleteDraftLease(portfolioViewId)
            if (viewData.leaseSelectorOptions.length > 1) {
              const firstAvailabeLeaseId = viewData.leaseSelectorOptions.find((x: any) => x.id !== portfolioViewId).id
              openPortfolio(firstAvailabeLeaseId)
              selectedPortfolioIndex = getSelectedPortfolioIndex(firstAvailabeLeaseId)
            } else {
              closeProperty()
            }
          },
        },
      ]
      break
    default:
      leaseOptions = []
  }

  return (
    <>
      <Helmet>
        <title>reOS | Leases | {viewData.title}</title>
      </Helmet>
      <DimmerLoader className={styles.root} loading={isPortfolioViewLoading} renderChildren={false} bgColor={'appBg'}>
        <header className={styles.header}>
          <ContextMenu name="leaseContextMenu" items={leaseOptions} position="left" showInput={false} />
          <button className={styles.close} onClick={closeProperty}>
            <Close />
          </button>
        </header>
        <div className={styles.body}>
          <div className="rbn--row">
            <div className="rbn--col rbn-col-md-8">
              <RefundProvider portfolioId={portfolioViewId}>
                <PortfolioInfo
                  redirect={redirect}
                  selectedPortfolio={pathOr(0, ['leaseSelectorOptions', selectedPortfolioIndex], viewData)}
                  onLeaseChange={(data: any) => redirect(`/leases/${data.id}`)}
                  leaseSelectorOptions={viewData.leaseSelectorOptions}
                  portfolio={portfolio}
                  tenantId={tenantId}
                />
              </RefundProvider>
            </div>
            <div className="rbn--col rbn-col-md-8">
              <PropertyInfo viewData={viewData} />
            </div>
          </div>
        </div>
      </DimmerLoader>
    </>
  )
}

export default subscribe(
  [PortfoliosProvider],
  ({
    portfolioViewId,
    redirect,
    closeProperty,
    viewData,
    selectedPortfolio,
    portfolioStatus,
    openEditPortfolio,
    openEditProperty,
    openPortfolioStatement,
    openRenewLease,
    deleteDraftLease,
    openPortfolio,
    openLeaseTermination,
    isPortfolioViewLoading,
    resetCurrentPortfolio,
  }: any) => ({
    portfolioViewId,
    redirect,
    closeProperty,
    viewData,
    selectedPortfolio,
    portfolioStatus,
    openEditPortfolio,
    openRenewLease,
    openEditProperty,
    openPortfolioStatement,
    deleteDraftLease,
    openPortfolio,
    openLeaseTermination,
    isPortfolioViewLoading,
    resetCurrentPortfolio,
  }),
)(PortfolioView)
